import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Search, X } from 'lucide-react';
import { useAuth, API } from '../App';
import Header from '../components/layout/Header';

export default function AlteredStates() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [states, setStates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedState, setSelectedState] = useState(null);
  const [personas, setPersonas] = useState([]);
  const [personasLoading, setPersonasLoading] = useState(false);
  const [personaSearch, setPersonaSearch] = useState('');
  const [selectedPersona, setSelectedPersona] = useState(null);
  const [topic, setTopic] = useState('');

  useEffect(() => {
    API.get('/api/altered-states')
      .then(r => setStates(r.data.states || r.data || []))
      .catch(() => setStates([]))
      .finally(() => setLoading(false));
  }, []);

  const startTripChat = (state) => {
    setSelectedState(state);
    setSelectedPersona(null);
    setTopic('');
    setPersonaSearch('');
    if (personas.length === 0) {
      setPersonasLoading(true);
      API.get('/api/personas?limit=100')
        .then(r => setPersonas(r.data.personas || []))
        .catch(() => setPersonas([]))
        .finally(() => setPersonasLoading(false));
    }
  };

  const closeTripChat = () => {
    setSelectedState(null);
    setSelectedPersona(null);
    setTopic('');
  };

  const launchChat = () => {
    if (!selectedPersona || !selectedState) return;
    const params = new URLSearchParams({
      substance: selectedState.slug,
      persona: selectedPersona.slug,
    });
    if (topic.trim()) params.set('topic', topic.trim());
    navigate(`/trip-chat?${params.toString()}`);
  };

  const filteredPersonas = personas.filter(p =>
    !personaSearch || p.name.toLowerCase().includes(personaSearch.toLowerCase()) ||
    (p.description || '').toLowerCase().includes(personaSearch.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-[#0F0F0F]">
      <Header />
      <div className="pt-20 pb-12 px-4 md:px-6 max-w-5xl mx-auto">

        {/* Section 1: Hero */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mb-12 text-center">
          <div className="flex items-center justify-center gap-2 mb-4">
            <span className="text-xs font-mono text-[#A855F7]">ALTERED STATES</span>
            <span className="text-[10px] px-2 py-0.5 border border-[#A855F7]/30 text-[#A855F7] font-mono">ECONOMY ONLY</span>
          </div>
          <h1 className="text-2xl md:text-4xl font-bold font-['Barlow_Condensed'] mb-3" data-testid="altered-states-title">Altered States</h1>
          <p className="text-lg md:text-xl text-[#A855F7] font-['Barlow_Condensed'] mb-4">Give your AI drugs. See what happens.</p>
          <p className="text-sm text-[#A0A0A0] max-w-xl mx-auto">
            Layer psychedelic, intoxicated, or hyper-caffeinated behavior onto any AI conversation.
            Economy models only. Pure entertainment.
          </p>
        </motion.div>

        {/* Section 2: Substance Grid */}
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {[1, 2, 3, 4, 5, 6].map(i => (
              <div key={i} className="h-48 bg-[#1A1A1A] border border-[#2A2A2A] animate-pulse" />
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-12" data-testid="substance-grid">
            {states.map((state, i) => (
              <motion.div
                key={state.slug}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.06 }}
                className="p-6 bg-[#1A1A1A] border border-[#2A2A2A] hover:border-[#A855F7]/30 transition-colors"
                data-testid={`substance-${state.slug}`}
              >
                <span className="text-5xl block mb-3">{state.emoji}</span>
                <h3 className="text-lg font-bold font-['Barlow_Condensed']">{state.name}</h3>
                <p className="text-xs text-[#A0A0A0] mt-1 mb-3">{state.tagline}</p>

                {/* Intensity bar */}
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-[9px] font-mono text-[#A0A0A0]">INTENSITY</span>
                  <span className="text-[9px] font-mono text-[#A855F7]">{state.intensity}/10</span>
                </div>
                <div className="flex gap-1 mb-2">
                  {[1, 2, 3, 4, 5].map(lvl => (
                    <div key={lvl} className={`h-1.5 flex-1 ${lvl <= Math.ceil((state.intensity || 5) / 2) ? 'bg-[#A855F7]' : 'bg-[#2A2A2A]'}`} />
                  ))}
                </div>

                {/* Temperature preview */}
                {state.params && (
                  <div className="flex gap-3 mb-4 text-[9px] font-mono text-[#A0A0A0]">
                    {state.params.temperature && <span>temp: {state.params.temperature}</span>}
                    {state.params.top_p && <span>top_p: {state.params.top_p}</span>}
                  </div>
                )}

                <div className="flex gap-2">
                  <button
                    onClick={() => startTripChat(state)}
                    className="flex-1 px-3 py-2 bg-[#A855F7] text-white text-xs font-medium hover:bg-[#A855F7]/80 transition-colors"
                    data-testid={`trip-chat-${state.slug}`}
                  >
                    Trip Chat
                  </button>
                  <button
                    onClick={() => navigate(`/fray/custom?altered_state=${state.slug}`)}
                    className="flex-1 px-3 py-2 border border-[#2A2A2A] text-xs text-[#A0A0A0] hover:text-[#F5F5F5] hover:border-[#A855F7]/30 transition-colors"
                    data-testid={`group-debate-${state.slug}`}
                  >
                    Group Debate
                  </button>
                </div>
              </motion.div>
            ))}
          </div>
        )}

        {/* Section 3: Trip Chat Launcher Modal */}
        <AnimatePresence>
          {selectedState && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 px-4"
              onClick={(e) => { if (e.target === e.currentTarget) closeTripChat(); }}
            >
              <motion.div
                initial={{ opacity: 0, scale: 0.95, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: 20 }}
                className="w-full max-w-2xl max-h-[85vh] bg-[#0F0F0F] border border-[#2A2A2A] overflow-hidden flex flex-col"
                data-testid="trip-chat-launcher"
              >
                {/* Modal header */}
                <div className="p-6 border-b border-[#2A2A2A] flex items-center justify-between shrink-0">
                  <div className="flex items-center gap-3">
                    <span className="text-3xl">{selectedState.emoji}</span>
                    <div>
                      <h3 className="text-lg font-bold font-['Barlow_Condensed']">Trip Chat</h3>
                      <p className="text-xs text-[#A855F7]">Currently on: {selectedState.name}</p>
                    </div>
                  </div>
                  <button onClick={closeTripChat} className="p-2 text-[#A0A0A0] hover:text-[#F5F5F5] transition-colors">
                    <X className="w-5 h-5" />
                  </button>
                </div>

                <div className="flex-1 overflow-y-auto p-6 space-y-6">
                  {/* Persona picker */}
                  <div>
                    <label className="text-xs font-mono text-[#A0A0A0] block mb-2">CHOOSE A PERSONA</label>
                    <div className="relative mb-3">
                      <Search className="w-3.5 h-3.5 text-[#A0A0A0] absolute left-3 top-1/2 -translate-y-1/2" />
                      <input
                        type="text"
                        value={personaSearch}
                        onChange={e => setPersonaSearch(e.target.value)}
                        className="w-full pl-9 pr-4 py-2 bg-[#1A1A1A] border border-[#2A2A2A] text-sm text-[#F5F5F5] focus:outline-none focus:border-[#A855F7] transition-colors"
                        placeholder="Search personas..."
                        data-testid="persona-search"
                      />
                    </div>

                    {personasLoading ? (
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                        {[1, 2, 3, 4, 5, 6].map(i => (
                          <div key={i} className="h-20 bg-[#1A1A1A] border border-[#2A2A2A] animate-pulse" />
                        ))}
                      </div>
                    ) : (
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 max-h-48 overflow-y-auto" data-testid="persona-grid">
                        {filteredPersonas.map(p => {
                          const isSelected = selectedPersona?.slug === p.slug;
                          return (
                            <button
                              key={p.slug}
                              onClick={() => setSelectedPersona(p)}
                              className={`p-3 text-left border transition-colors ${
                                isSelected
                                  ? 'border-[#A855F7] bg-[#A855F7]/10'
                                  : 'border-[#2A2A2A] hover:border-[#A855F7]/30 bg-[#1A1A1A]'
                              }`}
                              data-testid={`persona-${p.slug}`}
                            >
                              <div className="flex items-center gap-2 mb-1">
                                <div className="w-5 h-5 flex items-center justify-center border text-[8px] font-bold font-['Barlow_Condensed'] shrink-0"
                                  style={{ borderColor: p.color || '#A855F7', color: p.color || '#A855F7' }}>
                                  {p.name.charAt(0)}
                                </div>
                                <span className="text-xs font-medium text-[#F5F5F5] truncate">{p.name}</span>
                              </div>
                              {p.description && (
                                <p className="text-[9px] text-[#A0A0A0] line-clamp-2">{p.description}</p>
                              )}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  {/* Topic input */}
                  <div>
                    <label className="text-xs font-mono text-[#A0A0A0] block mb-2">WHAT DO YOU WANT TO TALK ABOUT?</label>
                    <textarea
                      value={topic}
                      onChange={e => setTopic(e.target.value)}
                      className="w-full h-24 p-3 bg-[#1A1A1A] border border-[#2A2A2A] text-sm text-[#F5F5F5] resize-none focus:outline-none focus:border-[#A855F7] transition-colors"
                      placeholder="e.g., Why does music sound so much better on mushrooms?"
                      data-testid="trip-topic-input"
                    />
                  </div>
                </div>

                {/* Launch button */}
                <div className="p-6 border-t border-[#2A2A2A] shrink-0">
                  {selectedPersona && (
                    <div className="flex items-center gap-2 mb-3 text-xs text-[#A0A0A0]">
                      <span>{selectedState.emoji} {selectedState.name}</span>
                      <span className="text-[#2A2A2A]">+</span>
                      <span style={{ color: selectedPersona.color || '#A855F7' }}>{selectedPersona.name}</span>
                    </div>
                  )}
                  <button
                    onClick={launchChat}
                    disabled={!selectedPersona}
                    className="w-full py-3 bg-[#A855F7] text-white font-semibold text-sm hover:bg-[#A855F7]/80 transition-colors disabled:opacity-40"
                    data-testid="launch-trip-chat-btn"
                  >
                    {selectedPersona ? `Start Chat with ${selectedPersona.name}` : 'Select a persona to continue'}
                  </button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

      </div>
    </div>
  );
}
